import { Component, OnInit, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { LoaderService } from 'src/app/component/loader-spinner/loader.service';
import { DatabaseService } from 'src/app/servicios/database/database.service';
import { FsessionService } from 'src/app/servicios/session/fsession.service';
import { MessageService } from 'src/app/servicios/message/message.service';
import { GroupedSelectGroup, SubmoduloRutaItem, buildGroupedSelectFromSubmodulos } from 'src/app/utils/default-route/default-route.util';

interface MunicipioRutaConfig {
  IdMunicipio: number;
  NombreMunicipio: string;
  IdRol: number;
  defaultRoute: string | null;
}

@Component({
  selector: 'app-dialogo-ruta-modo-predeterminado',
  templateUrl: './dialogo-ruta-modo-predeterminado.component.html',
  styleUrls: ['./dialogo-ruta-modo-predeterminado.component.scss'],
  standalone: false,
})
export class DialogoRutaModoPredeterminadoComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<DialogoRutaModoPredeterminadoComponent>);
  private readonly db = inject(DatabaseService);
  private readonly fse = inject(FsessionService);
  private readonly loaderService = inject(LoaderService);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);

  municipioControl = new FormControl<number | null>(null);
  routeGroups: GroupedSelectGroup[] = [];
  municipios: MunicipioRutaConfig[] = [];
  selectedRoute: string | null = null;
  esSuperAdminUser = false;
  saving = false;
  deleting = false;
  private originalRouteByMunicipio = new Map<number, string | null>();

  private readonly translateKeys = [
    'operacionExitosa',
    'errorInesperado',
    'error-guardar-rutas',
    'rutas-guardadas-exito',
    'rutas-borradas-exito',
    'confirmar-borrar-rutas',
    'ruta-no-permitida',
    'cancelar',
    'guardar',
    'si',
  ];
  private variables: Record<string, string> = {};

  ngOnInit(): void {
    this.translate.get(this.translateKeys).subscribe((res) => (this.variables = res));
    this.municipioControl.valueChanges.subscribe((idMunicipio) => {
      if (idMunicipio != null) {
        this.onMunicipioChanged(idMunicipio);
      }
    });
    this.loadData();
  }

  get hasSavedRoutes(): boolean {
    return this.municipios.some((municipio) => !!municipio.defaultRoute);
  }

  private loadData(): void {
    this.loaderService.showSpinner();
    this.db.obtenerMisRutasPredeterminadas().subscribe({
      next: (response) => {
        this.esSuperAdminUser = !!response?.esSuperAdmin;
        this.municipios = (response?.municipios ?? []).map((item: any) => {
          const defaultRoute = item.defaultRoute != null && item.defaultRoute !== '' ? String(item.defaultRoute) : null;
          this.originalRouteByMunicipio.set(Number(item.IdMunicipio), defaultRoute);
          return {
            IdMunicipio: Number(item.IdMunicipio),
            NombreMunicipio: item.NombreMunicipio,
            IdRol: Number(item.IdRol),
            defaultRoute,
          };
        });

        const currentMunicipio = Number(this.fse.optenerValor('dato4'));
        const defaultMunicipio = this.municipios.find((m) => m.IdMunicipio === currentMunicipio) ?? this.municipios[0];

        if (defaultMunicipio) {
          this.municipioControl.setValue(defaultMunicipio.IdMunicipio, { emitEvent: true });
        } else {
          this.loaderService.hideSpinner();
        }
      },
      error: () => {
        this.loaderService.hideSpinner();
        this.messageService.SwalMessage('error', this.variables['error-guardar-rutas'] || this.variables.errorInesperado);
      },
    });
  }

  private onMunicipioChanged(idMunicipio: number): void {
    const municipio = this.municipios.find((item) => item.IdMunicipio === idMunicipio);
    this.selectedRoute = municipio?.defaultRoute ?? null;
    this.loadRouteGroups(municipio?.IdRol);
  }

  private loadRouteGroups(idRol?: number): void {
    if (this.esSuperAdminUser) {
      this.db.obtenerTodosSubmodulosAgrupados().subscribe({
        next: (response) => {
          this.routeGroups = buildGroupedSelectFromSubmodulos(response?.submodulos ?? []);
          this.loaderService.hideSpinner();
        },
        error: () => {
          this.loaderService.hideSpinner();
          this.messageService.SwalMessage('error', this.variables.errorInesperado);
        },
      });
      return;
    }

    if (!idRol) {
      this.routeGroups = [];
      this.loaderService.hideSpinner();
      return;
    }

    this.db.obtenerSubmodulosPermitidosPorRol(idRol).subscribe({
      next: (response) => {
        this.routeGroups = buildGroupedSelectFromSubmodulos((response?.submodulos ?? []) as SubmoduloRutaItem[]);
        this.loaderService.hideSpinner();
      },
      error: () => {
        this.loaderService.hideSpinner();
        this.messageService.SwalMessage('error', this.variables.errorInesperado);
      },
    });
  }

  onRouteChange(value: string | null): void {
    this.selectedRoute = value;
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  save(): void {
    const idMunicipio = this.municipioControl.value;
    if (idMunicipio == null) {
      return;
    }

    const municipio = this.municipios.find((item) => item.IdMunicipio === idMunicipio);
    if (!municipio) {
      return;
    }

    this.saving = true;
    this.loaderService.showSpinner();
    this.db
      .guardarRutaPredeterminada({
        idMunicipio,
        defaultRoute: this.selectedRoute,
        idRol: municipio.IdRol,
      })
      .subscribe({
        next: () => {
          municipio.defaultRoute = this.selectedRoute;
          this.originalRouteByMunicipio.set(idMunicipio, this.selectedRoute);
          this.loaderService.hideSpinner();
          this.saving = false;
          this.messageService.SwalMessage('success', this.variables['rutas-guardadas-exito'] || this.variables.operacionExitosa);
          this.dialogRef.close(true);
        },
        error: (err) => {
          this.loaderService.hideSpinner();
          this.saving = false;
          const key = err?.error?.traslateError || 'error-guardar-rutas';
          this.translate.get(key).subscribe((message) => this.messageService.SwalMessage('error', message));
        },
      });
  }

  async deleteRoutes(): Promise<void> {
    const confirmed = await this.messageService.dialogMessage('question', this.variables['confirmar-borrar-rutas'], {
      confirmButtonText: this.variables.si || 'Si',
      cancelButtonText: this.variables.cancelar || 'Cancelar',
      showCancelButton: true,
    });

    if (!confirmed?.isConfirmed) {
      return;
    }

    this.deleting = true;
    this.loaderService.showSpinner();
    this.db.borrarRutasPredeterminadasUsuario().subscribe({
      next: () => {
        this.municipios.forEach((municipio) => {
          municipio.defaultRoute = null;
          this.originalRouteByMunicipio.set(municipio.IdMunicipio, null);
        });
        this.selectedRoute = null;
        this.loaderService.hideSpinner();
        this.deleting = false;
        this.messageService.SwalMessage('success', this.variables['rutas-borradas-exito'] || this.variables.operacionExitosa);
      },
      error: () => {
        this.loaderService.hideSpinner();
        this.deleting = false;
        this.messageService.SwalMessage('error', this.variables.errorInesperado);
      },
    });
  }
}
